import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Share2, Heart, Waves, Leaf, Sun, Target, Sparkles, Flame,
  Users, Compass, Palette,
} from 'lucide-react';
import { getManifestTheme, getAffirmationImage } from '../data/manifestData';
import AffirmationShareMenu from '../components/AffirmationShareMenu';
import AffirmationListenButton from '../components/AffirmationListenButton';
import ContentReactBar from '../components/ContentReactBar';

const iconMap = { Heart, Waves, Leaf, Sun, Target, Sparkles, Flame, Users, Compass, Palette };
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fetch the branded 16:9 posterImageUrl for each manifest-<theme>-<idx>
// affirmation record from the API. Returns an idx→url map. Falls back to
// the static image pool for themes / indexes that don't have a DB record.
const useEmblemPosters = (themeId) => {
  const [posters, setPosters] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!themeId) return;
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/content?type=affirmation&slugPrefix=manifest-${themeId}-&limit=100`);
        if (!r.ok) return;
        const d = await r.json();
        const slugRe = new RegExp(`^manifest-${themeId}-(\\d+)$`, 'i');
        const map = {};
        for (const item of (d.items || [])) {
          const m = slugRe.exec(item.slug || '');
          if (!m) continue;
          const idx = parseInt(m[1], 10);
          const u = item.posterImageUrl;
          if (u) {
            const abs = u.startsWith('http') ? u : `${API_URL}${u}`;
            // Resized WebP variant (~30 KB) instead of the ~600 KB PNG original.
            map[idx] = abs.includes('/api/media/') ? `${abs}?w=800` : abs;
          }
        }
        if (!cancelled) setPosters(map);
      } catch { /* ignore — static pool still works */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [themeId]);
  return { posters, loaded };
};

const ManifestThemePage = () => {
  const { themeId } = useParams();
  const navigate = useNavigate();
  const theme = getManifestTheme(themeId);
  const [shareIdx, setShareIdx] = useState(null); // index of the affirmation whose menu is open
  // Hook MUST be called on every render (React rules-of-hooks) — theme.id is
  // usually the same as themeId param, so we key on themeId here even if the
  // theme lookup fails, to keep the hook count stable across renders.
  const { posters: emblemPosters, loaded: postersLoaded } = useEmblemPosters(themeId);

  if (!theme) {
    return (
      <div className="p-8 text-center">
        <p className="rbr-serif-italic text-rbr-sage-deep">This theme could not be found.</p>
        <Link to="/manifest" className="rbr-btn-gold mt-6 inline-flex">Back to Manifest</Link>
      </div>
    );
  }

  const Icon = iconMap[theme.icon] || Sparkles;
  const imagePool = theme.images || [];
  // Branded emblem poster from the DB. While the poster list is still loading
  // we return null (neutral placeholder) instead of flashing the old static
  // illustration; static art is only used if the DB has no poster at all.
  const imageForIndex = (idx) => {
    if (emblemPosters[idx]) return emblemPosters[idx];
    if (!postersLoaded) return null;
    return getAffirmationImage(theme.id, idx) || imagePool[idx % Math.max(imagePool.length, 1)];
  };
  const activeShare = shareIdx !== null ? {
    affirmation: theme.affirmations[shareIdx],
    image: imageForIndex(shareIdx),
  } : null;

  // Healing / grief themes use a softer, muted palette — soft-sand background
  // with muted-green + cream accents — instead of the bright cover gradient.
  // This deliberately steers away from bright/cheerful colors on days when the
  // reader needs the design to hold them, not motivate them.
  const HEALING_THEME_IDS = new Set(['healing-and-release', 'peace', 'forgiveness']);
  const isHealing = HEALING_THEME_IDS.has(theme.id);
  const healingHero = isHealing
    ? { background: 'linear-gradient(160deg, #E8DDCB 0%, #C7CFB7 60%, #A3BEAF 100%)' }
    : null;
  const pageStyle = isHealing
    ? { background: 'linear-gradient(180deg, #F7F3EA 0%, #EEE8DA 100%)', minHeight: '100vh' }
    : undefined;

  return (
    <div
      className="pb-8"
      data-testid={`manifest-theme-${theme.id}`}
      data-healing-mode={isHealing ? 'true' : undefined}
      style={pageStyle}
    >
      {/* Hero — soft-sand + sage wash for healing themes; original gradient
          for everything else. Rings dimmed on healing themes so the surface
          reads as a soft blanket, not a marketing splash. */}
      <div
        className="relative overflow-hidden rounded-b-[2.5rem] shadow-rbr-lift"
        style={
          healingHero || {
            background: `linear-gradient(155deg, ${theme.gradient[0]} 0%, ${theme.gradient[1]} 100%)`,
          }
        }
      >
        <div
          className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: isHealing
              ? 'radial-gradient(circle, rgba(255,253,246,0.55) 0%, transparent 65%)'
              : 'radial-gradient(circle, rgba(255,255,255,0.30) 0%, transparent 65%)',
            opacity: isHealing ? 0.9 : 0.6,
          }}
          aria-hidden
        />
        <div
          className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ border: `1px solid ${isHealing ? 'rgba(120,155,131,0.35)' : 'rgba(255,255,255,0.15)'}` }}
          aria-hidden
        />
        <div
          className="absolute -top-20 -left-20 w-[300px] h-[300px] rounded-full pointer-events-none"
          style={{ border: `1px solid ${isHealing ? 'rgba(120,155,131,0.45)' : 'rgba(255,255,255,0.2)'}` }}
          aria-hidden
        />

        <div className="relative z-10 px-6 pt-6 pb-10">
          <button
            onClick={() => navigate('/manifest')}
            data-testid="manifest-theme-back-btn"
            className={`w-10 h-10 rounded-full backdrop-blur-md flex items-center justify-center transition-colors mb-6 ${
              isHealing
                ? 'bg-white/40 text-[#173F35] hover:bg-white/60'
                : 'bg-white/15 text-white hover:bg-white/25'
            }`}
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center mb-4">
            <Icon className="w-7 h-7 text-white" />
          </div>

          <p className="text-[10px] uppercase tracking-[0.32em] text-white/70 font-medium mb-2">
            Manifest
          </p>
          <h1
            className="text-white text-4xl font-semibold leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {theme.title}
          </h1>
          <p
            className="text-white/85 text-base"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          >
            {theme.tagline}
          </p>
        </div>
      </div>

      {/* Affirmations list — image + title + body per card */}
      <div className="px-5 mt-6">
        <p className="rbr-eyebrow mb-4 text-center">
          {theme.affirmations.length} affirmations
        </p>
        <div className="space-y-6">
          {theme.affirmations.map((a, idx) => {
            const img = imageForIndex(idx);
            return (
              <article
                key={idx}
                className="rbr-card rbr-card-lift overflow-hidden animate-fade-up"
                style={{ animationDelay: `${(idx % 6) * 0.05}s` }}
                data-testid={`manifest-affirmation-${theme.id}-${idx}`}
              >
                {/* Photo — the aspect box always renders so cards don't jump when posters arrive */}
                <div
                  className="relative w-full aspect-[16/10] overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${theme.gradient[0]}, ${theme.gradient[1]})` }}
                >
                  {img && (
                    <img
                      key={img}
                      src={img}
                      alt={a.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105 animate-fade-in"
                    />
                  )}
                  {/* Bottom gradient for possible caption */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: `linear-gradient(180deg, transparent 55%, ${theme.gradient[1]}22 100%)` }}
                    aria-hidden
                  />
                  {/* Accent bar */}
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ background: `linear-gradient(90deg, ${theme.accent}, ${theme.accent}55)` }}
                  />
                </div>

                {/* Text block */}
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3
                        className="rbr-heading text-xl leading-snug mb-2"
                        style={{ color: theme.gradient[1] }}
                      >
                        {a.title}
                      </h3>
                      <p className="text-rbr-blue-deep text-[15px] leading-relaxed">
                        {a.body}
                      </p>
                    </div>
                  </div>
                  {/* Universal social reactions — like + comment + share. */}
                  <div className="mt-3 pt-3 border-t border-stone-200/60 flex items-center justify-between gap-2">
                    <AffirmationListenButton
                      themeId={theme.id}
                      index={idx}
                      text={`${a.title}. ${a.body}`}
                      size="sm"
                      variant="onLight"
                    />
                    <ContentReactBar
                      contentRef={`manifest:${theme.id}:${idx}`}
                      size="sm"
                      variant="onLight"
                      share={{
                        kind: 'affirmation',
                        id: `${theme.id}-${idx}`,
                        title: a.title,
                        text: `"${a.title}" — a manifest affirmation from Reflections by Rowdy`,
                        url: typeof window !== 'undefined' ? `${window.location.origin}/a/${theme.id}/${idx}` : '',
                        recommendable: true,
                      }}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <p className="rbr-serif-italic text-center text-rbr-sage-deep text-sm mt-10 px-8 leading-relaxed">
        Repeat one gently, three times, before you move on with your day.
      </p>

      {activeShare && (
        <AffirmationShareMenu
          theme={theme}
          affirmation={activeShare.affirmation}
          index={shareIdx}
          image={activeShare.image}
          eyebrow={`Manifest · ${theme.title}`}
          onClose={() => setShareIdx(null)}
        />
      )}
    </div>
  );
};

export default ManifestThemePage;
